import { uniq } from 'lodash-es';
import { SovosDatePicker, SovosTextField } from 'mosaic-react';
import React, { useState } from 'react';

import {
	convertDateStringApiToUser,
	convertDateStringUserToApi,
} from 'src/helpers/convertDate';
import type { NewRateFileFormStep } from 'src/pages/newRateFile/NewRateFileFormTypes';

import FormStepLayout from './FormStepLayout';

const StepFileDetails: NewRateFileFormStep<{
	fileName?: string;
	setFileName: (fileName: string) => void;
	inputDate?: string | null;
	setInputDate: (inputDate?: string | null) => void;
}> = ({ onContinue, fileName, setFileName, inputDate, setInputDate }) => {
	const [showErrors, setShowErrors] = useState(false);

	const matchedForbiddenCharacters = fileName?.match(/[%\\?%*:/|"<>.]/g);

	const validations = [
		{
			isValid: Boolean(fileName && fileName.length > 0),
			message: 'Please enter a valid file name',
		},
		{
			isValid: !matchedForbiddenCharacters,
			message: `Special characters (${uniq(matchedForbiddenCharacters)
				?.sort()
				.join(', ')}) are not allowed in the file name`,
		},
		{
			isValid: Boolean(inputDate && inputDate.length > 0),
			message: 'Please enter a valid starting effective date',
		},
	];

	const errors = validations
		.filter(({ isValid }) => !isValid)
		.map(({ message }) => message);

	const handleContinue = () => {
		if (errors.length === 0) {
			onContinue();
		} else if (!showErrors) {
			setShowErrors(true);
		}
	};

	return (
		<FormStepLayout
			title='File details'
			subTitle='Enter file name'
			errors={errors}
			showErrors={showErrors}
			onContinue={handleContinue}
		>
			<SovosTextField
				required
				label='File name'
				inputProps={{ 'data-testid': 'test-checkbox' }}
				value={fileName || ''}
				onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
					setFileName(event.target.value)
				}
				error={
					showErrors && (!validations[0].isValid || !validations[1].isValid)
				}
				sx={{
					width: '275px',
					'&&& label': {
						color:
							showErrors && (!validations[0].isValid || !validations[1].isValid)
								? 'error.main'
								: undefined,
					},
				}}
			/>
			<SovosDatePicker
				label='Starting effective date'
				value={
					inputDate
						? convertDateStringApiToUser(inputDate)
						: showErrors
						? undefined
						: null
				}
				onChange={(dateValue?: string | null) =>
					setInputDate(
						dateValue ? convertDateStringUserToApi(dateValue) : dateValue
					)
				}
				TextFieldProps={{
					required: true,
					error: showErrors && !validations[2].isValid,
				}}
				sx={{
					width: '275px',
					'&&& label': {
						color:
							showErrors && !validations[2].isValid ? 'error.main' : undefined,
					},
					'&&& p': {
						color:
							showErrors && !validations[2].isValid ? 'error.main' : undefined,
					},
				}}
			/>
		</FormStepLayout>
	);
};

export default StepFileDetails;
